import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import {
  Send, FileSpreadsheet, Upload, Users, Plus, Pencil, Trash2, X, Save,
  FileText, Eye, Download, CheckCircle2, AlertCircle, Calendar, Database, BarChart3,
} from "lucide-react";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function AgentReports() {
  const { user } = useAuth();
  const canManage = (user?.permissions || []).includes("agent_reports.manage");
  const [tab, setTab] = useState("daily");

  if (!canManage) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="agent-reports-forbidden">Anda tidak memiliki permission <span className="font-mono text-cyan">agent_reports.manage</span>. Hubungi admin untuk request akses.</div>;
  }

  return (
    <div className="p-6 space-y-4 max-w-6xl animate-slide-up" data-testid="agent-reports-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Bot Broadcast</div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Send className="w-6 h-6 text-cyan" /> Agent Reports
        </h1>
        <p className="text-xs text-slate-400 mt-1 max-w-3xl">
          Upload performa merchant harian → data disimpan untuk evaluasi. Generate laporan mingguan / dua-mingguan / bulanan otomatis per agent dari data tersimpan, lalu kirim ke Telegram group agent.
        </p>
      </div>

      <div className="flex gap-1.5 border-b border-line" data-testid="tabs">
        <TabBtn active={tab === "daily"} onClick={() => setTab("daily")} testid="tab-daily"><Database className="w-3.5 h-3.5" /> Daily Performance</TabBtn>
        <TabBtn active={tab === "generate"} onClick={() => setTab("generate")} testid="tab-generate"><BarChart3 className="w-3.5 h-3.5" /> Generate Report</TabBtn>
        <TabBtn active={tab === "html"} onClick={() => setTab("html")} testid="tab-html"><FileText className="w-3.5 h-3.5" /> Upload HTML</TabBtn>
        <TabBtn active={tab === "recipients"} onClick={() => setTab("recipients")} testid="tab-recipients"><Users className="w-3.5 h-3.5" /> Recipients</TabBtn>
      </div>

      {tab === "daily" && <DailyPerformancePanel />}
      {tab === "generate" && <GenerateReportPanel />}
      {tab === "html" && <FromHtmlPanel />}
      {tab === "recipients" && <RecipientsPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
        active ? "text-cyan border-cyan" : "text-slate-400 border-transparent hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

const fmtIDR = (v) => "IDR " + Math.round(Number(v || 0)).toLocaleString();
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtRate = (a, s) => a > 0 ? ((s / a) * 100).toFixed(2) + "%" : "0.00%";

// ==========================================================================
// TAB 1: Daily Performance — upload + browse stored data
// ==========================================================================
function DailyPerformancePanel() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [filterMerchant, setFilterMerchant] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterService, setFilterService] = useState("");
  const [uploadService, setUploadService] = useState("PAYIN");
  const [loading, setLoading] = useState(false);

  const loadStats = async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/merchant-performance/stats"),
        api.get("/merchant-performance/merchants"),
      ]);
      setStats(s.data);
      setMerchants(m.data);
    } catch { /* ignore */ }
  };
  const loadRows = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      if (filterMerchant) params.merchant = filterMerchant;
      if (filterService) params.service_type = filterService;
      const { data } = await api.get("/merchant-performance", { params });
      setRows(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); loadRows(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [filterMerchant, filterFrom, filterTo, filterService]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setErr(""); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("service_type", uploadService);
      const { data } = await api.post("/merchant-performance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      await loadStats(); await loadRows();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const tok = localStorage.getItem("tara_token");
      const res = await fetch(`${API_BASE}/merchant-performance/csv-template`, { headers: { Authorization: `Bearer ${tok}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "merchant_daily_performance_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("Hapus baris data ini?")) return;
    try { await api.delete(`/merchant-performance/${id}`); loadRows(); loadStats(); }
    catch (e) { alert(formatErr(e)); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Upload Daily Performance CSV" testid="daily-upload-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Upload as</span>
              <div className="flex gap-1" data-testid="upload-service-toggle">
                {["PAYIN", "PAYOUT"].map((s) => (
                  <button
                    key={s}
                    data-testid={`upload-svc-${s}`}
                    onClick={() => setUploadService(s)}
                    className={`text-[10px] uppercase tracking-wider border px-2 py-1 rounded-sm ${uploadService === s ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                  >
                    {s === "PAYIN" ? "🅿️ PAYIN" : "💸 PAYOUT"}
                  </button>
                ))}
              </div>
            </div>
            <label className="block border-2 border-dashed border-line rounded-sm p-6 text-center cursor-pointer hover:border-cyan/50" data-testid="daily-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
              <div className="text-sm font-medium">Pilih file .csv performa harian</div>
              <div className="text-[11px] text-slate-500 mt-1">Kolom: performance_date, merchant_name, total_attempts, successful_transactions, successful_amount (+ optional failed/pending/median/p95)</div>
              <input ref={fileRef} type="file" accept=".csv" onChange={onPickFile} className="hidden" data-testid="daily-file-input" />
            </label>
            <button data-testid="dl-template-btn" onClick={downloadTemplate} className="mt-2 text-cyan hover:underline text-[11px] inline-flex items-center gap-1">
              <Download className="w-3 h-3" /> Download sample template
            </button>
          </div>
          <div className="border border-line rounded-sm p-3 bg-surface2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Storage Stats</div>
            {stats ? (
              <div className="space-y-1 text-xs font-mono">
                <div className="flex justify-between"><span className="text-slate-400">Total rows</span><span data-testid="stats-rows">{fmtInt(stats.total_rows)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Merchants</span><span data-testid="stats-merchants">{stats.merchants_count}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Earliest</span><span data-testid="stats-earliest">{stats.earliest_date || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Latest</span><span data-testid="stats-latest">{stats.latest_date || "—"}</span></div>
              </div>
            ) : <div className="text-xs text-slate-500">Loading...</div>}
          </div>
        </div>
        {busy && <div className="mt-3 text-xs text-slate-400 flex items-center gap-2"><div className="w-3 h-3 border-2 border-cyan border-t-transparent rounded-full animate-spin" /> Uploading & parsing...</div>}
        {err && <div className="mt-3 px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm" data-testid="daily-err">{err}</div>}
        {uploadResult && (
          <div className="mt-3 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3 text-xs" data-testid="upload-result">
            <div className="flex items-center gap-2 text-emerald-400 font-semibold">
              <CheckCircle2 className="w-4 h-4" /> Berhasil upload
            </div>
            <div className="text-slate-300 mt-1">
              <span className="font-mono">{uploadResult.inserted}</span> rows disimpan · <span className="font-mono">{uploadResult.merchants.length}</span> merchants · date range: <span className="font-mono">{uploadResult.date_range?.from} → {uploadResult.date_range?.to}</span>
            </div>
            {uploadResult.errors?.length > 0 && (
              <div className="mt-2 text-amber-400 text-[10px] font-mono space-y-0.5">
                {uploadResult.errors.slice(0, 5).map((e, i) => <div key={i}>⚠ {e}</div>)}
                {uploadResult.errors.length > 5 && <div>… +{uploadResult.errors.length - 5} more</div>}
              </div>
            )}
          </div>
        )}
      </Panel>

      <Panel title="Stored Performance Data" testid="daily-list-panel">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">From</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs" data-testid="filter-from" />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">To</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs" data-testid="filter-to" />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Merchant</label>
            <select value={filterMerchant} onChange={(e) => setFilterMerchant(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs w-48" data-testid="filter-merchant">
              <option value="">All</option>
              {merchants.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Service</label>
            <select value={filterService} onChange={(e) => setFilterService(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs" data-testid="filter-service">
              <option value="">All</option>
              <option value="PAYIN">PAYIN</option>
              <option value="PAYOUT">PAYOUT</option>
            </select>
          </div>
          <button onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterMerchant(""); setFilterService(""); }} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2" data-testid="filter-clear">Clear</button>
        </div>

        <div className="border border-line rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="daily-table">
            <thead className="bg-surface2 border-b border-line">
              <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Merchant</th>
                <th className="text-left px-3 py-2 font-medium">Service</th>
                <th className="text-right px-3 py-2 font-medium">Attempts</th>
                <th className="text-right px-3 py-2 font-medium">Success</th>
                <th className="text-right px-3 py-2 font-medium">Failed</th>
                <th className="text-right px-3 py-2 font-medium">Pending</th>
                <th className="text-right px-3 py-2 font-medium">Success Amount</th>
                <th className="text-right px-3 py-2 font-medium">Rate</th>
                <th className="text-right px-3 py-2 font-medium">Median/p95 (s)</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500 text-xs">Loading...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500 text-xs">Belum ada data.</td></tr>}
              {rows.map((r) => {
                const rate = r.total_attempts > 0 ? (r.successful_transactions / r.total_attempts) * 100 : 0;
                const svc = r.service_type || "PAYIN";
                return (
                  <tr key={r.id} className="border-b border-line/40 hover:bg-surface2" data-testid={`daily-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.performance_date}</td>
                    <td className="px-3 py-2 text-xs">{r.merchant_name}</td>
                    <td className="px-3 py-2"><Badge color={svc === "PAYOUT" ? "warning" : "info"}>{svc}</Badge></td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{fmtInt(r.total_attempts)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{fmtInt(r.successful_transactions)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-rose-400/70">{fmtInt(r.failed_transactions || 0)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-amber-400/70">{fmtInt(r.pending_transactions || 0)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{fmtIDR(r.successful_amount)}</td>
                    <td className="px-3 py-2 text-right"><Badge color={rate >= 90 ? "success" : rate >= 80 ? "warning" : "error"}>{rate.toFixed(2)}%</Badge></td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-500">{Math.round(r.median_processing_seconds || 0)} / {Math.round(r.p95_processing_seconds || 0)}</td>
                    <td className="px-3 py-2 text-right"><button data-testid={`del-daily-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-500 mt-2">Menampilkan versi terbaru per (tanggal, merchant). Setiap upload disimpan sebagai history (append mode).</div>
      </Panel>
    </div>
  );
}

// ==========================================================================
// TAB 2: Generate Report from DB
// ==========================================================================
function GenerateReportPanel() {
  const [recipients, setRecipients] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [brand, setBrand] = useState("TARAPAY");
  const [merchantsOverride, setMerchantsOverride] = useState("");
  const [format, setFormat] = useState("detail"); // detail | recap
  const [reports, setReports] = useState([]);
  const [perService, setPerService] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [previewIdx, setPreviewIdx] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [caption, setCaption] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [extraCsv, setExtraCsv] = useState("");
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => { api.get("/agent-recipients").then((r) => setRecipients(r.data)).catch(() => {}); }, []);

  const selectedAgent = recipients.find((r) => r.id === agentId);

  const applySuggestedRange = async (rid) => {
    if (!rid) return;
    try {
      const { data } = await api.get(`/agent-reports/suggest-range/${rid}`);
      setDateFrom(data.date_from); setDateTo(data.date_to);
    } catch { /* ignore */ }
  };

  const onAgentChange = (rid) => {
    setAgentId(rid); setReports([]); setSendResult(null);
    if (rid) applySuggestedRange(rid);
  };

  const generate = async () => {
    if (!agentId || !dateFrom || !dateTo) { setErr("Pilih agent + date range"); return; }
    setBusy(true); setErr(""); setReports([]); setPerService({}); setSendResult(null);
    try {
      const body = { agent_recipient_id: agentId, date_from: dateFrom, date_to: dateTo, brand_name: brand, format };
      const merchList = (merchantsOverride || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (merchList.length) body.merchants = merchList;
      const { data } = await api.post("/agent-reports/generate-from-db", body);
      setReports(data.reports || []);
      setPerService(data.per_service_reports || {});
      setSelectedIdx(new Set((data.reports || []).map((_, i) => i)));
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const previewReport = async (idx) => {
    setPreviewIdx(idx); setPreviewHtml("");
    try {
      const { data } = await api.post("/agent-reports/render", { report: reports[idx], brand_name: brand });
      setPreviewHtml(data.html);
    } catch (e) { setPreviewHtml(`<pre>${formatErr(e)}</pre>`); }
  };

  const previewRecap = async (svc) => {
    setPreviewIdx(`recap-${svc}`); setPreviewHtml("");
    try {
      const { data } = await api.post("/agent-reports/render-recap", {
        reports: perService[svc], service_type: svc, brand_name: brand,
        agent_name: recipients.find((r) => r.id === agentId)?.name,
      });
      setPreviewHtml(data.html);
    } catch (e) { setPreviewHtml(`<pre>${formatErr(e)}</pre>`); }
  };

  const downloadRecap = async (svc) => {
    try {
      const { data } = await api.post("/agent-reports/render-recap", {
        reports: perService[svc], service_type: svc, brand_name: brand,
        agent_name: recipients.find((r) => r.id === agentId)?.name,
      });
      const blob = new Blob([data.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.filename || `${svc}_recap.html`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(formatErr(e)); }
  };

  const downloadOne = async (idx) => {
    try {
      const { data } = await api.post("/agent-reports/render", { report: reports[idx], brand_name: brand });
      const blob = new Blob([data.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.filename || "agent_card.html";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(formatErr(e)); }
  };

  const toggleReport = (idx) => setSelectedIdx((s) => { const n = new Set(s); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });

  const send = async () => {
    setBusy(true); setErr(""); setSendResult(null);
    try {
      const selected = Array.from(selectedIdx).sort((a, b) => a - b).map((i) => reports[i]);
      const body = {
        reports: selected,
        brand_name: brand,
        caption,
        recipient_ids: selectedIds.length ? selectedIds : (agentId ? [agentId] : []),
        extra_chat_ids: parseExtra(extraCsv),
      };
      const { data } = await api.post("/agent-reports/send-generated", body);
      setSendResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Step 1 — Pick Agent & Date Range" testid="gen-step1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Agent Recipient *</label>
            <select value={agentId} onChange={(e) => onAgentChange(e.target.value)} className={ipt} data-testid="gen-agent-select">
              <option value="">— Pilih agent —</option>
              {recipients.filter((r) => r.active).map((r) => (
                <option key={r.id} value={r.id}>{r.name} · {r.billing_cycle || "weekly"} · {(r.merchants || []).length} merchant(s)</option>
              ))}
            </select>
            {selectedAgent && (
              <div className="text-[10px] text-slate-500 mt-1">
                Merchants: <span className="font-mono text-slate-300">{(selectedAgent.merchants || []).join(", ") || "—"}</span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Date From *</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={ipt} data-testid="gen-date-from" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Date To *</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={ipt} data-testid="gen-date-to" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Brand</label>
            <input value={brand} onChange={(e) => setBrand(e.target.value)} className={ipt} data-testid="gen-brand" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Report Format *</label>
            <div className="flex gap-1" data-testid="gen-format-toggle">
              {[
                { k: "detail", l: "Detail per merchant" },
                { k: "recap", l: "Recap table" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`gen-format-${opt.k}`}
                  onClick={() => setFormat(opt.k)}
                  className={`flex-1 text-xs uppercase tracking-wider border px-3 py-2 rounded-sm ${format === opt.k ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Merchants override (optional, comma-separated)</label>
            <input value={merchantsOverride} onChange={(e) => setMerchantsOverride(e.target.value)} placeholder="Kosong = pakai list dari agent" className={ipt} data-testid="gen-merchants-override" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button
            onClick={generate}
            disabled={busy || !agentId || !dateFrom || !dateTo}
            className="bg-cyan text-bg font-semibold px-5 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
            data-testid="gen-btn"
          >
            <BarChart3 className="w-4 h-4" /> {busy ? "Generating..." : "Generate Reports"}
          </button>
        </div>
      </Panel>

      {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm" data-testid="gen-err">{err}</div>}

      {reports.length > 0 && (
        <>
          {format === "recap" && (
            <Panel title="Recap Preview (per Service Type)" testid="gen-recap-preview">
              <div className="space-y-2">
                {Object.entries(perService).filter(([, arr]) => arr.length > 0).map(([svc, arr]) => {
                  const totalAtt = arr.reduce((s, r) => s + (r.totals?.attempts || 0), 0);
                  const totalSuc = arr.reduce((s, r) => s + (r.totals?.successful || 0), 0);
                  const totalAmt = arr.reduce((s, r) => s + (r.totals?.amount || 0), 0);
                  const rate = totalAtt > 0 ? (totalSuc / totalAtt * 100) : 0;
                  return (
                    <div key={svc} className="border border-line rounded-sm p-3" data-testid={`recap-${svc}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge color={svc === "PAYOUT" ? "warning" : "info"}>{svc === "PAYIN" ? "🅿️ PAYIN" : "💸 PAYOUT"}</Badge>
                          <span className="text-xs text-slate-400">{arr.length} merchants · {totalSuc.toLocaleString()}/{totalAtt.toLocaleString()} trx ({rate.toFixed(2)}%) · IDR {Math.round(totalAmt).toLocaleString()}</span>
                        </div>
                        <div className="flex gap-1">
                          <button data-testid={`recap-preview-${svc}`} onClick={() => previewRecap(svc)} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 flex items-center gap-1"><Eye className="w-3 h-3" /> Preview Recap</button>
                          <button data-testid={`recap-download-${svc}`} onClick={() => downloadRecap(svc)} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 flex items-center gap-1"><Download className="w-3 h-3" /> Download Recap</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {Object.values(perService).every((arr) => arr.length === 0) && (
                  <div className="text-xs text-slate-500 text-center py-4">Belum ada data untuk service_types agent ini di periode yang dipilih.</div>
                )}
              </div>
            </Panel>
          )}

          <Panel title={`Step 2 — Review Reports (${selectedIdx.size}/${reports.length} selected)`} testid="gen-step2">
            <div className="flex justify-end mb-2 gap-2">
              <button onClick={() => setSelectedIdx(new Set(reports.map((_, i) => i)))} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2" data-testid="gen-select-all">Select all</button>
              <button onClick={() => setSelectedIdx(new Set())} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2" data-testid="gen-clear">Clear</button>
            </div>
            <div className="border border-line rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[850px]" data-testid="gen-reports-table">
                <thead className="bg-surface2 border-b border-line">
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2 font-medium">Merchant</th>
                    <th className="text-left px-3 py-2 font-medium">Svc</th>
                    <th className="text-left px-3 py-2 font-medium">Period</th>
                    <th className="text-right px-3 py-2 font-medium">Days w/ data</th>
                    <th className="text-right px-3 py-2 font-medium">Attempts</th>
                    <th className="text-right px-3 py-2 font-medium">Successful</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="text-right px-3 py-2 font-medium">Rate</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r, i) => {
                    const daysWithData = (r.days || []).filter((d) => d.attempts > 0).length;
                    return (
                      <tr key={i} className="border-b border-line/40" data-testid={`gen-report-row-${i}`}>
                        <td className="px-3 py-2"><input type="checkbox" checked={selectedIdx.has(i)} onChange={() => toggleReport(i)} className="accent-cyan" data-testid={`gen-check-${i}`} /></td>
                        <td className="px-3 py-2 text-xs">{r.merchant}</td>
                        <td className="px-3 py-2"><Badge color={r.service_type === "PAYOUT" ? "warning" : "info"}>{r.service_type || "PAYIN"}</Badge></td>
                        <td className="px-3 py-2 text-[11px]">{r.period_label}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{daysWithData} / {r.days.length}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtInt(r.totals.attempts)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtInt(r.totals.successful)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtIDR(r.totals.amount)}</td>
                        <td className="px-3 py-2 text-right"><Badge color={r.totals.rate >= 90 ? "success" : r.totals.rate >= 80 ? "warning" : "error"}>{r.totals.rate.toFixed(2)}%</Badge></td>
                        <td className="px-3 py-2 text-right">
                          <button data-testid={`gen-preview-${i}`} onClick={() => previewReport(i)} className="text-slate-400 hover:text-cyan p-1" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                          <button data-testid={`gen-download-${i}`} onClick={() => downloadOne(i)} className="text-slate-400 hover:text-cyan p-1" title="Download HTML"><Download className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Step 3 — Recipients & Caption" testid="gen-step3">
            <div className="text-[11px] text-slate-500 mb-2">Default: kirim ke agent yang dipilih di Step 1. Bisa override dengan pilih recipient lain di bawah.</div>
            <RecipientPicker recipients={recipients} selectedIds={selectedIds} setSelectedIds={setSelectedIds} extraCsv={extraCsv} setExtraCsv={setExtraCsv} defaultAgentId={agentId} />
            <div className="mt-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Caption (optional; auto-generate per report kalau kosong)</label>
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} className={ipt} data-testid="gen-caption" />
            </div>
          </Panel>

          <div className="flex justify-end">
            {(() => {
              const recCount = (selectedIds.length ? selectedIds.length : (agentId ? 1 : 0)) + parseExtra(extraCsv).length;
              return (
                <button
                  data-testid="gen-send-btn"
                  onClick={send}
                  disabled={busy || selectedIdx.size === 0 || recCount === 0}
                  className="bg-cyan text-bg font-semibold px-6 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
                >
                  <Send className="w-4 h-4" /> {busy ? "Sending..." : `Send ${selectedIdx.size} report(s) × ${recCount} recipient(s)`}
                </button>
              );
            })()}
          </div>
        </>
      )}

      {sendResult && <SendResult result={sendResult} perReport />}
      {previewIdx !== null && <PreviewModal html={previewHtml || "<div style='padding:40px;text-align:center'>Rendering…</div>"} onClose={() => setPreviewIdx(null)} />}
    </div>
  );
}

// ==========================================================================
// TAB 3: Upload HTML directly
// ==========================================================================
function FromHtmlPanel() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [htmlText, setHtmlText] = useState("");
  const [caption, setCaption] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [extraCsv, setExtraCsv] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/agent-recipients").then((r) => setRecipients(r.data)).catch(() => {}); }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setErr(""); setResult(null);
    setHtmlText(await f.text());
  };
  const reset = () => { setFile(null); setHtmlText(""); setCaption(""); setResult(null); setErr(""); if (fileRef.current) fileRef.current.value = ""; };
  const send = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const body = { filename: file?.name || "report.html", html: htmlText, caption, recipient_ids: selectedIds, extra_chat_ids: parseExtra(extraCsv) };
      const { data } = await api.post("/agent-reports/send-html", body);
      setResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Panel title="Step 1 — Upload HTML File" testid="html-step-upload">
        {!file ? (
          <label className="block border-2 border-dashed border-line rounded-sm p-8 text-center cursor-pointer hover:border-cyan/50" data-testid="html-dropzone">
            <Upload className="w-8 h-8 mx-auto mb-3 text-slate-500" />
            <div className="text-sm font-medium">Pilih file .html untuk dikirim</div>
            <div className="text-xs text-slate-500 mt-1">Max 10 MB · dikirim sebagai document attachment ke Telegram</div>
            <input ref={fileRef} type="file" accept=".html,.htm" onChange={onPickFile} className="hidden" data-testid="html-file-input" />
          </label>
        ) : (
          <div className="flex items-center justify-between bg-surface2 border border-line rounded-sm p-3" data-testid="html-file-info">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-cyan" />
              <div>
                <div className="text-sm font-medium">{file.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{(file.size / 1024).toFixed(1)} KB · {htmlText.length} chars</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPreviewOpen(true)} className="border border-line hover:bg-bg px-3 py-1 rounded-sm text-xs flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</button>
              <button onClick={reset} className="text-xs text-slate-400 hover:text-rose-400 border border-line px-3 py-1 rounded-sm">Reset</button>
            </div>
          </div>
        )}
      </Panel>

      {file && (
        <>
          <Panel title="Step 2 — Recipients & Caption" testid="html-step-recipients">
            <RecipientPicker recipients={recipients} selectedIds={selectedIds} setSelectedIds={setSelectedIds} extraCsv={extraCsv} setExtraCsv={setExtraCsv} />
            <div className="mt-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Caption (optional, supports HTML)</label>
              <textarea data-testid="html-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} className={ipt} />
            </div>
          </Panel>

          {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}

          <div className="flex justify-end">
            <button
              data-testid="html-send-btn"
              onClick={send}
              disabled={busy || (!selectedIds.length && !parseExtra(extraCsv).length) || !htmlText.trim()}
              className="bg-cyan text-bg font-semibold px-6 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {busy ? "Sending..." : `Send to ${selectedIds.length + parseExtra(extraCsv).length} recipient(s)`}
            </button>
          </div>
        </>
      )}

      {result && <SendResult result={result} />}
      {previewOpen && <PreviewModal html={htmlText} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}

// ==========================================================================
// TAB 4: Recipients CRUD
// ==========================================================================
function RecipientsPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [merchantOptions, setMerchantOptions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/agent-recipients"),
        api.get("/merchant-performance/merchants").catch(() => ({ data: [] })),
      ]);
      setList(r.data);
      setMerchantOptions(m.data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!confirm(`Hapus recipient "${r.name}"?`)) return;
    try { await api.delete(`/agent-recipients/${r.id}`); load(); } catch (e) { alert(formatErr(e)); }
  };

  return (
    <Panel title="Agent Recipients" testid="recipients-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400">Daftarkan group Telegram / chat_id agent yang menerima laporan.</p>
        <button
          data-testid="add-recipient-btn"
          onClick={() => setEditing({ name: "", chat_id: "", thread_id: "", merchants: [], billing_cycle: "weekly", notes: "", active: true })}
          className="bg-cyan text-bg font-semibold px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5 hover:bg-cyan-dark"
        >
          <Plus className="w-3.5 h-3.5" /> Add Recipient
        </button>
      </div>
      {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm mb-3">{err}</div>}
      {loading && <div className="text-xs text-slate-400">Loading...</div>}
      {!loading && list.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid="empty-recipients">Belum ada recipient. Klik &quot;Add Recipient&quot; untuk mulai.</div>
      )}
      {!loading && list.length > 0 && (
        <div className="border border-line rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid="recipients-table">
            <thead className="bg-surface2 border-b border-line">
              <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <th className="text-left px-3 py-2.5 font-medium">Name</th>
                <th className="text-left px-3 py-2.5 font-medium">Chat ID</th>
                <th className="text-left px-3 py-2.5 font-medium">Cycle</th>
                <th className="text-left px-3 py-2.5 font-medium">Merchants</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-b border-line/40 hover:bg-surface2" data-testid={`recipient-row-${r.id}`}>
                  <td className="px-3 py-2.5"><div className="text-white">{r.name}</div>{r.notes && <div className="text-[10px] text-slate-500">{r.notes}</div>}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-cyan">{r.chat_id}{r.thread_id && <span className="text-slate-500"> · t{r.thread_id}</span>}</td>
                  <td className="px-3 py-2.5"><Badge color="info">{(r.billing_cycle || "weekly").toUpperCase()}</Badge></td>
                  <td className="px-3 py-2.5 text-xs max-w-md">{(r.merchants || []).length > 0 ? r.merchants.join(", ") : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5"><Badge color={r.active ? "success" : "muted"}>{r.active ? "ACTIVE" : "INACTIVE"}</Badge></td>
                  <td className="px-3 py-2.5 text-right">
                    <button data-testid={`edit-recipient-${r.id}`} onClick={() => setEditing(r)} className="text-slate-400 hover:text-cyan p-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-recipient-${r.id}`} onClick={() => del(r)} className="text-slate-400 hover:text-rose-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <RecipientEditor initial={editing} merchantOptions={merchantOptions} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Panel>
  );
}

function RecipientEditor({ initial, merchantOptions, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, merchants_csv: (initial.merchants || []).join(", "), service_types: initial.service_types || ["PAYIN"] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isNew = !form.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        name: form.name,
        chat_id: form.chat_id,
        thread_id: form.thread_id || null,
        merchants: (form.merchants_csv || "").split(",").map((s) => s.trim()).filter(Boolean),
        billing_cycle: form.billing_cycle || "weekly",
        service_types: form.service_types && form.service_types.length ? form.service_types : ["PAYIN"],
        notes: form.notes || "",
        active: form.active !== false,
      };
      if (isNew) await api.post("/agent-recipients", body);
      else await api.put(`/agent-recipients/${form.id}`, body);
      onSaved();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const availableToAdd = useMemo(() => {
    const current = new Set((form.merchants_csv || "").split(",").map((s) => s.trim()).filter(Boolean));
    return (merchantOptions || []).filter((m) => !current.has(m));
  }, [merchantOptions, form.merchants_csv]);
  const addMerchant = (m) => {
    const cur = (form.merchants_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
    set("merchants_csv", [...cur, m].join(", "));
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="recipient-editor">
      <div className="bg-surface border border-line rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h3 className="text-sm font-semibold">{isNew ? "Add Recipient" : "Edit Recipient"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Name *</label>
              <input data-testid="rcpt-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={ipt} placeholder="e.g. DW" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Billing Cycle *</label>
              <select data-testid="rcpt-cycle" value={form.billing_cycle || "weekly"} onChange={(e) => set("billing_cycle", e.target.value)} className={ipt}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly (14 days)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Service Types * <span className="text-slate-500 normal-case">(agent dapat komisi dari service apa)</span></label>
            <div className="flex gap-2" data-testid="rcpt-services">
              {["PAYIN", "PAYOUT"].map((s) => {
                const active = (form.service_types || []).includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`rcpt-svc-${s}`}
                    onClick={() => {
                      const cur = form.service_types || [];
                      const next = active ? cur.filter((x) => x !== s) : [...cur, s];
                      set("service_types", next.length ? next : [s]);
                    }}
                    className={`text-xs uppercase tracking-wider border px-4 py-2 rounded-sm flex items-center gap-1.5 ${active ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                  >
                    {s === "PAYIN" ? "🅿️ PAYIN" : "💸 PAYOUT"}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Chat ID *</label>
              <input data-testid="rcpt-chatid" value={form.chat_id || ""} onChange={(e) => set("chat_id", e.target.value)} className={`${ipt} font-mono`} placeholder="-1001234567890" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Thread ID (optional)</label>
              <input data-testid="rcpt-thread" value={form.thread_id || ""} onChange={(e) => set("thread_id", e.target.value)} className={`${ipt} font-mono`} placeholder="123" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Merchants (comma-separated)</label>
            <input data-testid="rcpt-merchants" value={form.merchants_csv || ""} onChange={(e) => set("merchants_csv", e.target.value)} className={ipt} placeholder="Hanoman, AS, Zeus" />
            {availableToAdd.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider mr-1">Quick add:</span>
                {availableToAdd.slice(0, 20).map((m) => (
                  <button key={m} onClick={() => addMerchant(m)} className="text-[10px] border border-line px-1.5 py-0.5 rounded-sm hover:bg-surface2 hover:border-cyan" data-testid={`add-merch-${m}`}>+ {m}</button>
                ))}
                {availableToAdd.length > 20 && <span className="text-[10px] text-slate-600">+ {availableToAdd.length - 20} more…</span>}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Notes (optional)</label>
            <input data-testid="rcpt-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className={ipt} />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input data-testid="rcpt-active" type="checkbox" checked={form.active !== false} onChange={(e) => set("active", e.target.checked)} className="accent-cyan" />
            Active
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line bg-surface2">
          <button onClick={onClose} className="border border-line hover:bg-bg px-4 py-2 rounded-sm text-xs">Cancel</button>
          <button data-testid="save-recipient" onClick={save} disabled={busy || !form.name || !form.chat_id} className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-xs flex items-center gap-1.5 hover:bg-cyan-dark disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Shared components
// ==========================================================================
function RecipientPicker({ recipients, selectedIds, setSelectedIds, extraCsv, setExtraCsv, defaultAgentId }) {
  const toggle = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAll = () => setSelectedIds(recipients.filter((r) => r.active).map((r) => r.id));
  const clear = () => setSelectedIds([]);
  const activeCount = recipients.filter((r) => r.active).length;
  return (
    <div className="space-y-2" data-testid="recipient-picker">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
          Registered Recipients ({activeCount} active){defaultAgentId && <span className="text-slate-500 normal-case ml-2">· Default: agent yang dipilih di Step 1</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={selectAll} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2" data-testid="rcpt-select-all">Select all</button>
          <button onClick={clear} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2" data-testid="rcpt-clear">Clear</button>
        </div>
      </div>
      {recipients.length === 0 ? (
        <div className="text-xs text-slate-500 border border-dashed border-line rounded-sm px-3 py-4 text-center">Belum ada recipient terdaftar.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-52 overflow-y-auto border border-line rounded-sm p-2">
          {recipients.map((r) => (
            <label key={r.id} className={`flex items-start gap-2 p-1.5 rounded-sm cursor-pointer hover:bg-surface2 ${!r.active ? "opacity-40" : ""} ${defaultAgentId === r.id ? "bg-cyan/5 border border-cyan/20" : ""}`} data-testid={`pick-rcpt-${r.id}`}>
              <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggle(r.id)} disabled={!r.active} className="accent-cyan mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{r.name} {defaultAgentId === r.id && <span className="text-cyan text-[9px]">DEFAULT</span>}</div>
                <div className="text-[10px] font-mono text-slate-500">{r.chat_id}{r.thread_id ? ` · thread ${r.thread_id}` : ""} · {r.billing_cycle || "weekly"}</div>
              </div>
            </label>
          ))}
        </div>
      )}
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Extra chat IDs (comma or newline)</label>
        <textarea data-testid="extra-chat-ids" value={extraCsv} onChange={(e) => setExtraCsv(e.target.value)} rows={2} className={`${ipt} font-mono text-[11px]`} placeholder="-1001234567890" />
      </div>
    </div>
  );
}

function parseExtra(csv) {
  return (csv || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
}

function PreviewModal({ html, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-surface border border-line rounded-sm w-full max-w-6xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-line">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Eye className="w-4 h-4" /> Preview</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="preview-close"><X className="w-4 h-4" /></button>
        </div>
        <iframe title="preview" srcDoc={html} className="w-full flex-1 bg-white" data-testid="preview-iframe" sandbox="allow-same-origin" />
      </div>
    </div>
  );
}

function SendResult({ result, perReport }) {
  return (
    <Panel title="Send Result" testid="send-result">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sent</div>
          <div className="text-2xl font-bold font-mono text-emerald-400 mt-1" data-testid="result-sent">{result.sent}</div>
        </div>
        <div className="border border-rose-500/30 bg-rose-500/5 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-rose-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Failed</div>
          <div className="text-2xl font-bold font-mono text-rose-400 mt-1" data-testid="result-failed">{result.failed}</div>
        </div>
      </div>
      {perReport && result.per_report && (
        <div className="space-y-2">
          {result.per_report.map((pr, i) => (
            <div key={i} className="border border-line rounded-sm p-3 text-xs" data-testid={`pr-result-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono font-semibold">{pr.agent} · {pr.merchant}</span>
                <span className="text-slate-500">{pr.filename}</span>
              </div>
              {pr.error && <div className="text-rose-400 text-[11px]">{pr.error}</div>}
              <div className="space-y-0.5 mt-1">
                {pr.results?.map((r, j) => (
                  <div key={j} className="flex items-center justify-between text-[11px] font-mono">
                    <span className="text-slate-400">→ {r.recipient} ({r.chat_id})</span>
                    <span className={r.ok ? "text-emerald-400" : "text-rose-400"}>{r.ok ? "✓ sent" : `✗ ${r.error}`}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {!perReport && result.results && (
        <div className="space-y-1">
          {result.results.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] font-mono border border-line rounded-sm px-3 py-1.5">
              <span className="text-slate-400">→ {r.recipient} ({r.chat_id})</span>
              <span className={r.ok ? "text-emerald-400" : "text-rose-400"}>{r.ok ? "✓ sent" : `✗ ${r.error}`}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
